// GA4 and the Meta pixel, both gated on an explicit opt-in. Ported from the
// Slova HR setup, minus its dev-property juggling: an unset VITE_GA_ID or
// VITE_FB_PIXEL_ID means that tag never loads, which is what a local build and
// a preview host get.
//
// This sits beside analytics.js, which is the first-party scan counter and is
// NOT consent-gated: it stores no identifier and the server aggregates it per
// day, so it keeps running whatever is decided here.

import { isAndroidApp } from '../platform'

const CONSENT_KEY = 'cookie_consent'
const GA_ID = import.meta.env.VITE_GA_ID || ''
const FB_PIXEL_ID = import.meta.env.VITE_FB_PIXEL_ID || ''

// Every event carries the platform, which is how the Android traffic gets
// separated in GA4. See platform.js for why only the bridge is trusted.
const PLATFORM = isAndroidApp ? 'android' : 'web'

// The android build leaves VITE_FB_PIXEL_ID unset, so the consent dialog must
// not name a tag that will never load: a disclosure that does not match what
// the app does is the likeliest Play rejection cause, and it is the dialog a
// reviewer reads.
export const pixelConfigured = Boolean(FB_PIXEL_ID)

function read(key) {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

export function hasConsentDecision() {
  return Boolean(read(CONSENT_KEY))
}

export function hasFullConsent() {
  return read(CONSENT_KEY) === 'all'
}

export function shouldShowConsentBanner() {
  return !hasConsentDecision()
}

function injectGA4() {
  if (!GA_ID || document.getElementById('ga4-script')) return

  // The queue has to exist before the async script: gtag pushes to dataLayer
  // and gtag.js drains it on load. Building it in onload instead drops every
  // event fired in between.
  window.dataLayer = window.dataLayer || []
  window.gtag = window.gtag || function gtag() { window.dataLayer.push(arguments) }

  const script = document.createElement('script')
  script.id = 'ga4-script'
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`
  document.head.appendChild(script)

  window.gtag('js', new Date())
  window.gtag('config', GA_ID, {
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    app_platform: PLATFORM,
  })
  window.gtag('set', 'user_properties', { app_platform: PLATFORM })
}

function injectFBPixel() {
  if (!FB_PIXEL_ID || window.fbq) return

  const fbq = function fbq(...args) {
    fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args)
  }
  window.fbq = fbq
  window._fbq = fbq
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)

  window.fbq('init', FB_PIXEL_ID)
  window.fbq('track', 'PageView')
}

/** Call once on boot: loads the tags only if consent was already given. */
export function initAnalyticsIfConsented() {
  if (hasFullConsent()) {
    injectGA4()
    injectFBPixel()
  }
}

export function acceptAll() {
  try {
    localStorage.setItem(CONSENT_KEY, 'all')
  } catch {
    // A blocked store means the decision cannot be remembered; load the tags
    // for this page anyway, since the user just said yes.
  }
  injectGA4()
  injectFBPixel()
}

export function rejectAll() {
  try {
    localStorage.setItem(CONSENT_KEY, 'necessary')
  } catch {
    // The decision cannot be remembered, but the tags still have to stop for
    // this page — silence below does not depend on the write succeeding.
  }
  silenceTags()
}

// Withdrawing consent mid-session has to stop collection now, not at the next
// reload. A script element cannot be unloaded, so instead: flip GA4's own
// documented kill switch, tell it consent was revoked, and let track() refuse
// to send anything further. The pixel has no equivalent switch, so trackPixel
// gating it is the whole mechanism there.
function silenceTags() {
  if (typeof window === 'undefined') return
  if (GA_ID) window[`ga-disable-${GA_ID}`] = true
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    })
  }
}

export function clearConsentDecision() {
  try {
    localStorage.removeItem(CONSENT_KEY)
  } catch {
    // Ignore.
  }
}

/**
 * A GA4 event. Gated on the stored decision, not on whether a tag happens to
 * be loaded: after a withdrawal the script is still in the page, so checking
 * window.gtag alone kept sending events until the next reload.
 */
export function track(name, params = {}) {
  if (!hasFullConsent()) return
  if (window.gtag) window.gtag('event', name, { ...params, app_platform: PLATFORM })
}

/** A Meta pixel event, for the few that have a standard equivalent. */
export function trackPixel(name, params = {}) {
  if (!hasFullConsent()) return
  if (window.fbq) window.fbq('track', name, params)
}
